import React, { useCallback, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Form, Formik, type FormikHelpers } from "formik";
import { Archive, ArchiveRestore, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TextAreaInput, TextInput } from "common-tailwind-components";

import {
  create_category,
  formatApiRequestError,
  get_categories,
  splitApiRequestFormErrors,
  update_category,
} from "../../api";
import type { CategoryKind, ClassificationCategory } from "../../api/types";
import { useLogin } from "../../reducers";
import { isAdmin } from "../../utils/permissions";
import Modal from "../Modal";
import {
  AddButton,
  Badge,
  FieldLabel,
  ModalButtons,
  fieldClass,
  iconButtonClass,
  summaryClass,
} from "./ui";

const KINDS: CategoryKind[] = ["step_type", "business_domain", "process_pattern"];

/** The server refuses to archive below this many active categories of a kind. */
const MIN_ACTIVE = 2;

type CategoryFormValues = { name: string; description: string };

const categoryFormFields = ["name", "description"] as const;

const emptyValues: CategoryFormValues = { name: "", description: "" };

const applyCategoryFormError = (
  err: unknown,
  t: ReturnType<typeof useTranslation>["t"],
  helpers: FormikHelpers<CategoryFormValues>,
  setError: (error: string | null) => void,
) => {
  const parsed = splitApiRequestFormErrors<CategoryFormValues>(err, t, categoryFormFields);
  helpers.setTouched(parsed.touched, false);
  helpers.setErrors(parsed.fieldErrors);
  setError(parsed.remainder);
  helpers.setSubmitting(false);
};

const inlineFieldClass =
  "w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary";

/** The in-row editor: name and description with save/cancel; Escape cancels.
 * `onSave` failures stay in the editor. */
const CategoryEditor = ({
  initialValues,
  onSave,
  onCancel,
}: {
  initialValues: CategoryFormValues;
  onSave: (values: CategoryFormValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (
    values: CategoryFormValues,
    helpers: FormikHelpers<CategoryFormValues>,
  ) => {
    setError(null);
    try {
      await onSave(values);
    } catch (err) {
      applyCategoryFormError(err, t, helpers, setError);
    }
  };

  return (
    <Formik<CategoryFormValues> initialValues={initialValues} onSubmit={onSubmit}>
      {({ isSubmitting, values }) => (
        <Form
          onKeyDown={e => {
            if (e.key === "Escape") onCancel();
          }}
          className="space-y-2"
        >
          <TextInput
            name="name"
            wrapped={false}
            unstyled
            className={inlineFieldClass}
            ariaLabel={t("category_name")}
            disabled={isSubmitting}
            required
          />
          <TextAreaInput
            name="description"
            wrapped={false}
            unstyled
            className={`min-h-20 ${inlineFieldClass}`}
            placeholder={t("category_description")}
            disabled={isSubmitting}
          />
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="rounded-md border border-outline px-2.5 py-1 text-sm text-text-dim transition-colors hover:text-text"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !values.name.trim() || !values.description.trim()}
              className="rounded-md bg-primary px-2.5 py-1 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("save")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

/** The add dialog for one kind; closes itself on success. */
const AddCategoryModal = ({
  kind,
  open,
  onOpenChange,
  onCreate,
}: {
  kind: CategoryKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (values: CategoryFormValues) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (
    values: CategoryFormValues,
    helpers: FormikHelpers<CategoryFormValues>,
  ) => {
    setError(null);
    try {
      await onCreate(values);
      helpers.resetForm();
      onOpenChange(false);
    } catch (err) {
      applyCategoryFormError(err, t, helpers, setError);
    }
  };

  return (
    <Formik<CategoryFormValues> initialValues={emptyValues} onSubmit={onSubmit}>
      {({ isSubmitting, values, resetForm }) => (
        <Modal
          open={open}
          onOpenChange={nextOpen => {
            if (!nextOpen) {
              resetForm();
              setError(null);
            }
            onOpenChange(nextOpen);
          }}
          busy={isSubmitting}
        >
          <div className="pr-10">
            <Dialog.Title className="text-lg font-semibold text-text">
              {t("category_add")}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-mid">
              {t(`category_kind_${kind}`)}
            </Dialog.Description>
          </div>
          <Form className="mt-5 space-y-3">
            <FieldLabel label={t("category_name")}>
              <TextInput
                name="name"
                wrapped={false}
                unstyled
                className={fieldClass}
                disabled={isSubmitting}
                required
              />
            </FieldLabel>
            <FieldLabel label={t("category_description")}>
              <TextAreaInput
                name="description"
                wrapped={false}
                unstyled
                className={`min-h-24 ${fieldClass}`}
                disabled={isSubmitting}
              />
            </FieldLabel>
            {error && <p className="text-sm text-danger">{error}</p>}
            <ModalButtons
              submitLabel={t("category_add")}
              disabled={isSubmitting}
              submitDisabled={!values.name.trim() || !values.description.trim()}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

/** One category: its name, disclosing the description, with edit and
 * (un)archive for managers. */
const CategoryRow = ({
  category,
  canManage,
  archiveBlocked,
  onUpdate,
}: {
  category: ClassificationCategory;
  canManage: boolean;
  /** Archiving it would leave too few active categories of its kind. */
  archiveBlocked: boolean;
  onUpdate: (values: CategoryFormValues & { archived: boolean }) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (editing) {
    return (
      <li className="py-1">
        <CategoryEditor
          initialValues={{ name: category.name, description: category.description }}
          onSave={async values => {
            await onUpdate({ ...values, archived: category.archived });
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  const toggleArchived = async () => {
    setError(null);
    try {
      await onUpdate({
        name: category.name,
        description: category.description,
        archived: !category.archived,
      });
    } catch (err) {
      setError(formatApiRequestError(err, t));
    }
  };

  const archiveLabel = category.archived ? t("category_unarchive") : t("category_archive");
  const archiveDisabled = !category.archived && archiveBlocked;

  return (
    <li>
      <div className="flex items-start justify-between gap-2">
        <details className={category.archived ? "min-w-0 opacity-70" : "min-w-0"}>
          <summary className="cursor-pointer py-0.5 text-sm text-text-mid transition-colors hover:text-text">
            {category.name}
            {category.archived && (
              <span className="ml-2">
                <Badge label={t("category_archived_badge")} />
              </span>
            )}
          </summary>
          <p className="mb-1 ml-4 text-sm text-text-dim">{category.description}</p>
        </details>
        {canManage && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              title={t("vocabulary_edit_entry")}
              aria-label={t("vocabulary_edit_entry")}
              className={`${iconButtonClass} p-1`}
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => void toggleArchived()}
              disabled={archiveDisabled}
              title={
                archiveDisabled ? t("category_min_active", { count: MIN_ACTIVE }) : archiveLabel
              }
              aria-label={archiveLabel}
              className={`${iconButtonClass} p-1`}
            >
              {category.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            </button>
          </div>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </li>
  );
};

/** One kind's card, laid out like a process card: the title, then its
 * categories (active first) collapsed behind a "{n} categories" disclosure. */
const CategoryList = ({
  kind,
  categories,
  canManage,
  onCreate,
  onUpdate,
}: {
  kind: CategoryKind;
  categories: ClassificationCategory[];
  canManage: boolean;
  onCreate: (values: CategoryFormValues) => Promise<void>;
  onUpdate: (id: string, values: CategoryFormValues & { archived: boolean }) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const active = categories.filter(category => !category.archived);
  const archived = categories.filter(category => category.archived);

  return (
    <div className="rounded-xl border border-outline bg-surface p-4">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <span className="text-sm font-medium text-text">{t(`category_kind_${kind}`)}</span>
        {canManage && <AddButton label={t("category_add")} onClick={() => setAdding(true)} />}
      </div>
      <details className="mt-2">
        <summary className={summaryClass}>
          {t("n_categories", { count: categories.length })}
        </summary>
        <ul className="mt-1 space-y-0.5">
          {[...active, ...archived].map(category => (
            <CategoryRow
              key={category.id}
              category={category}
              canManage={canManage}
              archiveBlocked={active.length <= MIN_ACTIVE}
              onUpdate={values => onUpdate(category.id, values)}
            />
          ))}
        </ul>
      </details>
      {canManage && (
        <AddCategoryModal kind={kind} open={adding} onOpenChange={setAdding} onCreate={onCreate} />
      )}
    </div>
  );
};

/** The company's classification categories, one card per kind. Everyone sees
 * them; vocabulary managers add, edit and (un)archive. */
const Categories = () => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const [categories, setCategories] = useState<ClassificationCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setCategories(await get_categories());
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const canManage = isAdmin(permissions);

  return (
    <section className="mt-10">
      <h2 className="text-lg font-medium text-text">{t("vocabulary_categories")}</h2>
      <p className="mb-3 mt-1 text-sm text-text-mid">{t("vocabulary_categories_description")}</p>
      {error && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-strong">
          {t("failed_to_load_vocabulary")}: {error}
        </div>
      )}
      {!categories && !error && (
        <div className="text-sm text-text-dim">{t("loading_ellipsis")}</div>
      )}
      {categories && (
        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
          {KINDS.map(kind => (
            <CategoryList
              key={kind}
              kind={kind}
              categories={categories.filter(category => category.kind === kind)}
              canManage={canManage}
              onCreate={async values => {
                await create_category({ kind, ...values });
                await load();
              }}
              onUpdate={async (id, values) => {
                await update_category(id, values);
                await load();
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Categories;
